import React, { useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Role } from "../@types";
import { useGetAllRolesQuery } from "../userApiSlice";

interface RoleDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  uiError?: string;
}

interface RoleOption {
  value: string;
  label: string;
}

const RolesDropdown: React.FC<RoleDropdownProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Select a role",
  name = "user-role",
  id = "user-role",
  showLabel = true,
  uiError,
}) => {
  const { data, isLoading, error, isError } = useGetAllRolesQuery();

  const roles = useMemo(() => {
    return data?.data?.data || [];
  }, [data?.data]);

  const rolesOptions: RoleOption[] = React.useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading available roles" }];
    }

    return roles.map((typeObj: Role) => ({
      value: typeObj.name,
      label: typeObj.name,
    }));
  }, [roles, isLoading, isError]);

  const handleValueChange = (option: RoleOption) => {
    onChange?.(option.value);
  };

  // Helper functions for MySelect
  const getValue = (option: RoleOption) => option.value;
  const getName = (option: RoleOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<RoleOption>
        name={name}
        id={id}
        label={showLabel ? "Role" : ""}
        options={rolesOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={onChange}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
      />

      {isError && (
        <p className="mt-1 text-sm text-primary" role="alert">
          {getErrorDetails(error).message || "Error loading available roles"}
        </p>
      )}

      {uiError && (
        <p className="mt-1 text-sm text-primary" role="alert">
          {uiError}
        </p>
      )}
    </div>
  );
};

export default RolesDropdown;
